// Steps 2-4 of route A: the uniform k-mesh and the bands, the projections (orbitals per element, as in the
// Slater-Koster set-up, or hybrids), and the windows on the DFT run's bands coloured by how much each state is made of
// the chosen orbitals. Every change is checked on the server (POST /wannier/check) with the DFT run's bands.
import { Alert, Button, Checkbox, Chip, Group, NumberInput, SegmentedControl, Select, SimpleGrid, Stack, Table, Text, Textarea,
  TextInput } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { ErrorBox } from "../../components/Errors";
import { Plot } from "../../components/Plot";
import type { Limits, ProjectDetail } from "../../lib/api";
import type { QeIssue } from "../../lib/qe";
import type { DftResult } from "../../lib/skfitter";
import { countWann, draggedWindows, PROJECTION_GROUPS, projectionsFromDefaults, suggestMesh, wannierWindows, windowsFigure, type WannierCheck,
  type WannierMemory, type Windows } from "../../lib/wannier";
import type { Remember } from "./WannierWorkspace";

type Dft = (DftResult & { meta: { sites?: { symbol: string; frac: number[] }[] } }) | undefined;

export function Issues({ issues, only }: { issues: QeIssue[] | undefined; only?: (i: QeIssue) => boolean }) {
  const list = (issues ?? []).filter((i) => (only ? only(i) : true) && i.level !== "note");
  if (!list.length) return null;
  const errors = list.filter((i) => i.level === "error"), warnings = list.filter((i) => i.level === "warning");
  return (
    <Stack gap={4} data-testid="wannier-issues">
      {errors.length > 0 && <Alert color="red" p="xs" title="Errors - the recipe cannot run">
        {errors.map((i) => <Text key={i.code + i.message} size="xs" c="red.9">{i.message}</Text>)}</Alert>}
      {warnings.length > 0 && <Alert color="orange" p="xs" title="Warnings - it runs, but check these">
        {warnings.map((i) => <Text key={i.code + i.message} size="xs" c="orange.9">{i.message}</Text>)}</Alert>}
    </Stack>
  );
}

const nav = (onBack: () => void, onNext: () => void, next: string, disabled = false) => (
  <Group justify="space-between"><Button variant="default" onClick={onBack}>Back</Button>
    <Button onClick={onNext} disabled={disabled}>{next}</Button></Group>
);

// == 2 the mesh ========================================================================================================
export function MeshStep({ mem, remember, dft, check, limits, onBack, onNext }: {
  project: ProjectDetail; mem: WannierMemory; remember: Remember; dft: Dft; check?: WannierCheck; limits: Limits;
  onBack: () => void; onNext: () => void;
}) {
  const lattice = dft?.meta.lattice, frac = (dft?.meta.sites ?? []).map((s) => s.frac);
  const quick = lattice ? suggestMesh(lattice, frac, 0.55) : null, accurate = lattice ? suggestMesh(lattice, frac, 0.27) : null;
  const nk = mem.mesh[0] * mem.mesh[1] * mem.mesh[2];
  const maxK = Number(limits["wannier.max_kpoints"] ?? 0);
  const setN = (i: number, v: number | string) => { const m = [...mem.mesh] as [number, number, number]; m[i] = Math.max(1, Math.round(Number(v) || 1)); remember({ mesh: m }); };
  return (
    <Stack gap="sm" mt="md">
      <Text size="sm" c="dimmed">Wannier90 works on a uniform k-mesh (the nscf computes it in full, without symmetry). A finer mesh gives
        a model that is also right between the mesh points, at the cost of time; a layer or a wire needs one k-point across the vacuum.</Text>
      <Group gap="xs" align="flex-end">
        {[0, 1, 2].map((i) => <NumberInput key={i} w={90} size="sm" label={`n${i + 1}`} min={1} max={64} value={mem.mesh[i]}
          onChange={(v) => setN(i, v)} data-testid={`mesh-${i}`} />)}
        {quick && <Button size="sm" variant="light" onClick={() => remember({ mesh: quick })}>Quick: {quick.join("×")}</Button>}
        {accurate && <Button size="sm" variant="light" onClick={() => remember({ mesh: accurate })}>Accurate: {accurate.join("×")}</Button>}
      </Group>
      <Text size="sm" data-testid="mesh-size">{nk} k-points{maxK ? ` (your plan allows ${maxK})` : ""}. Roughly {Math.max(1, Math.round(nk * 0.3))} s to{" "}
        {Math.max(1, Math.round(nk * 1.3 / 60))} min on the server (0.3–1.3 s per k-point in our tests: silicon to bcc iron).</Text>
      {maxK > 0 && nk > maxK && <Alert color="red" p="xs"><Text size="xs">More k-points than your plan allows: use a coarser mesh.</Text></Alert>}
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <NumberInput label="Bands for Wannier90 (num_bands)" description={`empty: as many as Wannier functions${dft ? ` · the DFT run has ${dft.meta.nbands}` : ""}`}
          min={1} value={mem.num_bands ?? ""} onChange={(v) => remember({ num_bands: v === "" ? null : Math.round(Number(v)) })} />
        <TextInput label="Leave out bands (exclude_bands)" description='low semicore states, e.g. "1-5"' value={mem.exclude_bands ?? ""}
          onChange={(e) => remember({ exclude_bands: e.currentTarget.value })} />
      </SimpleGrid>
      {!!check?.num_wann && <Text size="sm" c="dimmed">{check.num_wann} Wannier functions from {check.num_bands} bands
        {check.num_bands != null && check.num_bands > check.num_wann ? " - disentanglement: set the windows in step 4" : ""}.</Text>}
      <Issues issues={check?.issues} only={(i) => /^(mesh|num_bands|exclude_bands|limit\.max_kpoints|limit\.max_bands|bands)/.test(i.code)} />
      {nav(onBack, onNext, "Next: projections", maxK > 0 && nk > maxK)}
    </Stack>
  );
}

// == 3 the projections ================================================================================================
export function ProjectionsStep({ mem, remember, dft, check, onBack, onNext }: {
  mem: WannierMemory; remember: Remember; dft: Dft; check?: WannierCheck; onBack: () => void; onNext: () => void;
}) {
  const elements = dft?.elements ?? [];
  const counts = Object.fromEntries(elements.map((e) => [e.symbol, e.count]));
  const local = countWann(mem.projections, counts, mem.spin === "spinor" ? "spinor" : "none");
  const [expert, setExpert] = useState(!!(mem.expert ?? "").trim());
  // a new DFT run starts from SKFitter's default orbitals
  useEffect(() => {
    if (elements.length && !Object.keys(mem.projections).length && !(mem.expert ?? "").trim())
      remember({ projections: projectionsFromDefaults(elements) });
  }, [elements.length]); // eslint-disable-line react-hooks/exhaustive-deps
  const nw = mem.scdm ? (mem.num_wann ?? 0) : (check?.num_wann ?? local);
  const scdm = mem.scdm;
  return (
    <Stack gap="sm" mt="md">
      <SegmentedControl size="xs" value={scdm ? "scdm" : "orbitals"} data-testid="projection-kind"
        data={[{ value: "orbitals", label: "Orbitals per element" }, { value: "scdm", label: "Automatic (SCDM)" }]}
        onChange={(v) => remember(v === "scdm" ? { scdm: { entanglement: mem.windows ? "erfc" : "isolated", mu: 1, sigma: 2 }, pd: null }
          : { scdm: null })} />
      {scdm ? (
        <Stack gap="xs" data-testid="scdm">
          <Text size="sm" c="dimmed">SCDM (selected columns of the density matrix; Damle, Lin and Ying 2015, Vitale et al. 2020):
            pw2wannier90.x builds the starting functions from the Bloch states themselves - no choice of orbitals. Give how many
            functions; for entangled bands, which states count (a smooth cut at μ of width σ: erfc, or a window around μ: gaussian).</Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <NumberInput label="num_wann" min={1} value={mem.num_wann ?? ""} data-testid="scdm-num-wann"
              onChange={(v) => remember({ num_wann: v === "" ? null : Math.round(Number(v)) })} />
            <Select label="Bands" value={scdm.entanglement} allowDeselect={false}
              data={[{ value: "isolated", label: "isolated" }, { value: "erfc", label: "entangled: erfc" }, { value: "gaussian", label: "entangled: gaussian" }]}
              onChange={(v) => v && remember({ scdm: { ...scdm, entanglement: v as "isolated" | "erfc" | "gaussian" } })} />
            {scdm.entanglement !== "isolated" && <>
              <NumberInput label="μ (from E_F)" value={scdm.mu} step={0.5} decimalScale={3} rightSection={<Text size="xs" c="dimmed">eV</Text>}
                onChange={(v) => remember({ scdm: { ...scdm, mu: Number(v) } })} />
              <NumberInput label="σ" value={scdm.sigma} min={0.01} step={0.5} decimalScale={3} rightSection={<Text size="xs" c="dimmed">eV</Text>}
                onChange={(v) => remember({ scdm: { ...scdm, sigma: Number(v) || 1 } })} />
            </>}
          </SimpleGrid>
          {scdm.entanglement === "isolated" && <Text size="xs" c="dimmed">Isolated: num_bands = num_wann (step 2), the other bands
            in exclude_bands; no windows.</Text>}
        </Stack>
      ) : <>
      <Text size="sm" c="dimmed">The orbitals each Wannier function starts from (Wannier90's projections): per element, as in the
        Slater-Koster set-up. Hybrids (sp³ for the bonds of silicon, sp² for graphene's σ bonds) give bond-centred functions.</Text>
      <Table withTableBorder data-testid="projections">
        <Table.Thead><Table.Tr><Table.Th>Element</Table.Th><Table.Th>Projections</Table.Th><Table.Th>Functions</Table.Th></Table.Tr></Table.Thead>
        <Table.Tbody>
          {elements.map((e) => {
            const chosen = mem.projections[e.symbol] ?? [];
            return (
              <Table.Tr key={e.symbol}>
                <Table.Td><Text fw={600}>{e.symbol}</Text><Text size="xs" c="dimmed">{e.count} atom{e.count > 1 ? "s" : ""}</Text></Table.Td>
                <Table.Td>
                  <Chip.Group multiple value={chosen} onChange={(v) => remember({ projections: { ...mem.projections, [e.symbol]: v } })}>
                    <Stack gap={4}>
                      {PROJECTION_GROUPS.map((g) => (
                        <Group key={g.label} gap={4}>
                          <Text size="xs" c="dimmed" w={70}>{g.label}</Text>
                          {g.names.map((n) => <Chip key={n} value={n} size="xs" data-testid={`proj-${e.symbol}-${n}`}>{n}</Chip>)}
                        </Group>
                      ))}
                    </Stack>
                  </Chip.Group>
                </Table.Td>
                <Table.Td>{countWann({ [e.symbol]: chosen }, counts, mem.spin === "spinor" ? "spinor" : "none")}</Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
      <Group gap="xs">
        <Button size="xs" variant="light" onClick={() => remember({ projections: projectionsFromDefaults(elements) })}>Slater-Koster defaults</Button>
        <Button size="xs" variant="subtle" onClick={() => setExpert((x) => !x)}>{expert ? "Hide" : "Show"} Wannier90 projection lines (expert)</Button>
      </Group>
      {expert && (
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <Textarea label="Projection lines" description="added to the ones above, e.g. f=0.25,0.25,0.25:s or Si:l=0:r=2" autosize minRows={2}
            value={mem.expert ?? ""} onChange={(e) => remember({ expert: e.currentTarget.value })} />
          <NumberInput label="num_wann" description="needed with projection lines" min={1} value={mem.num_wann ?? ""}
            onChange={(v) => remember({ num_wann: v === "" ? null : Math.round(Number(v)) })} />
        </SimpleGrid>
      )}
      </>}
      <Text size="sm" data-testid="num-wann"><b>{nw}</b> Wannier function{nw === 1 ? "" : "s"} per spin channel
        {mem.num_bands ? ` from ${mem.num_bands} bands` : ""}.</Text>
      <Issues issues={check?.issues} only={(i) => /^(projections|num_wann|scdm|limit\.max_wann)/.test(i.code)} />
      {nav(onBack, onNext, "Next: windows", nw < 1)}
    </Stack>
  );
}

// == 4 the windows =====================================================================================================
export function WindowsStep({ project, mem, remember, check, onBack, onNext }: {
  project: ProjectDetail; mem: WannierMemory; remember: Remember; check?: WannierCheck; onBack: () => void; onNext: () => void;
}) {
  const projections = useMemo(() => Object.fromEntries(Object.entries(mem.projections).filter(([, v]) => v.length)), [mem.projections]);
  const q = useQuery({ queryKey: ["wannier-windows", mem.dft_run, JSON.stringify(projections)],
    queryFn: () => wannierWindows(mem.dft_run!, projections, project.id), enabled: !!mem.dft_run && Object.keys(projections).length > 0,
    retry: false });
  const channels = Object.keys(q.data?.channels ?? {});
  const [spin, setSpin] = useState("up");
  const pd = mem.pd && !mem.scdm ? mem.pd : null;
  const mode = pd ? "pd" : mem.windows ? "yes" : "no";
  const disentangle = !!mem.windows && !pd;
  const w = mem.windows ?? q.data?.windows ?? { outer: [-10, 5] as [number, number], frozen: [-10, 0] as [number, number] };
  const set = (k: "outer" | "frozen", i: 0 | 1, v: number | string) => {
    const cur: Windows = { outer: [...w.outer] as [number, number], frozen: w.frozen ? ([...w.frozen] as [number, number]) : undefined };
    const arr = (cur[k] ?? [...cur.outer]) as [number, number];
    arr[i] = Number(v);
    cur[k] = arr;
    remember({ windows: cur });
  };
  const lo = Math.min(w.outer[0], -4) - 2, hi = Math.max(w.outer[1], 4) + 2;
  return (
    <Stack gap="sm" mt="md">
      <Text size="sm" c="dimmed">With more bands than Wannier functions, Wannier90 picks the subspace (disentanglement): the <b>outer
        window</b> holds every state that may contribute, the <b>frozen window</b> the states reproduced exactly. Without windows the
        chosen bands must be isolated from the others.</Text>
      <SegmentedControl value={mode} data-testid="disentangle"
        data={[{ value: "no", label: "Isolated bands (no windows)" }, { value: "yes", label: "Disentanglement (windows)" },
          ...(mem.scdm ? [] : [{ value: "pd", label: "By projectability" }])]}
        onChange={(v) => remember(v === "pd" ? { pd: { min: 0.01, max: 0.95, frozen: false }, windows: mem.windows ?? q.data?.windows ?? w }
          : { pd: null, windows: v === "yes" ? (mem.windows ?? q.data?.windows ?? w) : null })} />
      {pd && (
        <Stack gap={4} data-testid="pd">
          <Text size="sm" c="dimmed">Projectability disentanglement (Qiao, Pizzi and Marzari 2023): every state is judged by how much of it
            lies on the chosen orbitals (projwfc.x on the Wannier mesh) - kept exactly above <b>max</b>, left out below <b>min</b>, mixed
            in as needed between. No energy windows to choose; colour on the plot is that weight.</Text>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <NumberInput label="Frozen above" value={pd.max} min={0} max={1} step={0.01} decimalScale={3}
              onChange={(v) => remember({ pd: { ...pd, max: Number(v) } })} />
            <NumberInput label="Left out below" value={pd.min} min={0} max={1} step={0.01} decimalScale={3}
              onChange={(v) => remember({ pd: { ...pd, min: Number(v) } })} />
          </SimpleGrid>
          <Checkbox size="xs" label="Also freeze the states in the frozen energy window (e.g. up to E_F + 2 eV for a metal)" checked={pd.frozen}
            onChange={(e) => remember({ pd: { ...pd, frozen: e.currentTarget.checked }, windows: { outer: w.outer, frozen: w.frozen ?? [w.outer[0], 1] } })} />
          {pd.frozen && <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <NumberInput label="Frozen: bottom" value={w.frozen?.[0] ?? ""} step={0.1} decimalScale={3} onChange={(v) => set("frozen", 0, v)} rightSection={<Text size="xs" c="dimmed">eV</Text>} />
            <NumberInput label="Frozen: top" value={w.frozen?.[1] ?? ""} step={0.1} decimalScale={3} onChange={(v) => set("frozen", 1, v)} rightSection={<Text size="xs" c="dimmed">eV</Text>} />
          </SimpleGrid>}
        </Stack>
      )}
      {q.error ? <ErrorBox error={q.error} /> : q.data && (
        <>
          {channels.length > 1 && <SegmentedControl size="xs" value={spin} onChange={setSpin} data={channels.map((c) => ({ value: c, label: `spin ${c}` }))} />}
          <Plot {...windowsFigure(q.data, channels.includes(spin) ? spin : channels[0], disentangle ? w : pd?.frozen && w.frozen ? ({ frozen: w.frozen } as unknown as Windows) : null,
            [lo, hi], disentangle)} height={420}
            onRelayout={(u) => { const nw = disentangle ? draggedWindows(w, u) : null; if (nw) remember({ windows: nw }); }} />
          {disentangle && <Text size="xs" c="dimmed">Drag the edges of the shaded windows (orange: outer, blue: frozen), or type the energies below.</Text>}
          {q.data.message && <Alert color="orange" p="xs"><Text size="xs">{q.data.message}</Text></Alert>}
        </>
      )}
      {disentangle && (
        <>
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            <NumberInput label="Outer: bottom" value={w.outer[0]} step={0.1} decimalScale={3} onChange={(v) => set("outer", 0, v)} rightSection={<Text size="xs" c="dimmed">eV</Text>} />
            <NumberInput label="Outer: top" value={w.outer[1]} step={0.1} decimalScale={3} onChange={(v) => set("outer", 1, v)} rightSection={<Text size="xs" c="dimmed">eV</Text>} />
            <NumberInput label="Frozen: bottom" value={w.frozen?.[0] ?? ""} step={0.1} decimalScale={3} onChange={(v) => set("frozen", 0, v)} rightSection={<Text size="xs" c="dimmed">eV</Text>} />
            <NumberInput label="Frozen: top" value={w.frozen?.[1] ?? ""} step={0.1} decimalScale={3} onChange={(v) => set("frozen", 1, v)} rightSection={<Text size="xs" c="dimmed">eV</Text>} />
          </SimpleGrid>
          <Group gap="xs">
            <Text size="xs" c="dimmed">Energies relative to E_F of the DFT run.</Text>
            {q.data?.windows && <Button size="compact-xs" variant="light" onClick={() => remember({ windows: q.data!.windows })}
              data-testid="use-suggestion">Use the suggestion ({q.data.windows.outer.map((v) => v.toFixed(2)).join(" … ")}
              {q.data.windows.frozen ? `; frozen ${q.data.windows.frozen.map((v) => v.toFixed(2)).join(" … ")}` : ""})</Button>}
            {w.frozen && <Button size="compact-xs" variant="subtle" onClick={() => remember({ windows: { outer: w.outer } })}>No frozen window</Button>}
          </Group>
        </>
      )}
      <Issues issues={check?.issues} only={(i) => /^windows/.test(i.code)} />
      {nav(onBack, onNext, "Next: run")}
    </Stack>
  );
}
